import { useTranslation } from 'react-i18next';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { Chip, Stack } from '@mui/material';

import type { CatalogLanguage } from '../../model/types';

export interface CardLanguageSwitcherProps {
  /** Enabled registry languages, default first — see `useCardLanguageDraft().enabledLanguages`. */
  languages: CatalogLanguage[];
  selectedLocale: string;
  onSelect: (locale: string) => void;
  /** Whether a locale's draft currently has any content — renders a filled/empty indicator (never colour-only). */
  isFilled: (locale: string) => boolean;
  disabled?: boolean;
}

/**
 * Compact chip-row language switcher for a multilingual master-data card (product/category —
 * epic-catalog-004, adr-tenant-005, plan-tenant-004 Wave 2). Distinct from `LocalizedFieldsEditor`
 * (which stacks every language's field at once, for dictionary entities like attributes/families):
 * this switches the card's visible fields to ONE language at a time, for the larger product/
 * category forms. Renders nothing when the tenant has no ENABLED catalog languages — the caller
 * falls back to today's single-language field (see `useCardLanguageDraft`'s doc).
 */
export function CardLanguageSwitcher({
  languages,
  selectedLocale,
  onSelect,
  isFilled,
  disabled,
}: CardLanguageSwitcherProps) {
  const { t } = useTranslation('catalog');

  if (languages.length === 0) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      role="group"
      aria-label={t('cardLanguageSwitcher.groupAriaLabel')}
    >
      {languages.map((language) => {
        const selected = language.locale === selectedLocale;
        const filled = isFilled(language.locale);
        const label = language.defaultLocale
          ? `${language.name} · ${t('cardLanguageSwitcher.defaultSuffix')}`
          : language.name;
        const status = t(filled ? 'cardLanguageSwitcher.filledStatus' : 'cardLanguageSwitcher.emptyStatus');
        return (
          <Chip
            key={language.locale}
            label={label}
            icon={
              filled ? (
                <CheckCircleRoundedIcon fontSize="small" />
              ) : (
                <RadioButtonUncheckedRoundedIcon fontSize="small" />
              )
            }
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'filled' : 'outlined'}
            onClick={() => onSelect(language.locale)}
            disabled={disabled}
            aria-pressed={selected}
            aria-label={t('cardLanguageSwitcher.chipAriaLabel', { name: label, status })}
          />
        );
      })}
    </Stack>
  );
}
